import React, { useState, useRef, useEffect } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import styled from "styled-components";
import axios from "axios";
import { Paragraph } from "../Typography";

const apiUrl =
  "https://apim-assembly-web.azure-api.net/api/v1/outreach_requests?subscription-key=786b410d9c844837aa80f2ec4351f3ae";

const validateValues = (values) => {
  const errors = {};
  if (!values.name) {
    errors.name = "Please provide your name.";
  }
  if (!values.company) {
    errors.company = "Please provide your company name.";
  }
  if (!values.email) {
    errors.email = "Please enter your email address.";
  } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(values.email)) {
    errors.email = "Invalid email address";
  }
  return errors;
};

const FormFieldContainer = styled.div`
  border: 1px solid ${({ theme }) => theme.colours.lightGrey};
  padding: 10px;
  margin-bottom: 16px;
  border-color: ${({ $isFocused, theme }) =>
    $isFocused ? theme.colours.blue : theme.colours.lightGrey};
`;

const Label = styled.label`
  font-size: 14px;
  line-height: 18px;
  font-family: "supply", monospace;
  font-weight: 400;
  color: ${({ theme }) => theme.colours.lightGrey};
`;

const Input = styled(Field)`
  border: none;
  outline: none;
  color: ${({ theme }) => theme.colours.black};
  font-family: ${({ theme }) => theme.fontFamily};
  font-size: 18px;
  line-height: 22px;
  font-weight: 400;
  width: 100%;
  padding: 16px 0;

  &:focus {
    font-size: 18px;
    line-height: 22px;
    font-weight: 400;
    border: none;
    outline: none;
  }
`;

const Button = styled.button`
  &[type="submit"] {
    font-family: ${({ theme }) => theme.fontFamilyMono};
    text-transform: uppercase;
    height: 44px;
    background-color: ${({ theme }) => theme.colours.blue};
    border-radius: 70px;
    border: none;
    color: white;
    font-size: 16px;
    line-height: 100%;
    padding: 12px 32px 14px;
    min-width: 128px;
    cursor: pointer;
    margin-bottom: 30px;

    &:disabled {
      background-color: ${({ theme }) => theme.colours.lightGrey};
      cursor: not-allowed;
    }
  }
`;

const Error = styled(ErrorMessage)`
  font-size: 14px;
  margin-top: 7px;
  color: ${({ theme }) => theme.colours.red};
`;

function FormikForm({ doFocusFirstFieldOnLoad, successMessage }) {
  const nameInputRef = useRef(null);
  const emailInputRef = useRef(null);
  const [focusedField, setFocusedField] = useState(null);

  useEffect(() => {
    if (doFocusFirstFieldOnLoad && !focusedField && nameInputRef.current) {
      nameInputRef.current.focus();
    }
  }, [focusedField, doFocusFirstFieldOnLoad]);

  return (
    <Formik
      initialValues={{
        name: "",
        email: "",
        company: "",
      }}
      validateOnChange={false}
      validateOnBlur={false}
      validate={(values) => validateValues(values)}
      onSubmit={(values, { setSubmitting, setStatus }) => {
        setStatus(null);
        setSubmitting(true);

        if (window && window.grecaptcha) {
          window.grecaptcha.ready(function () {
            window.grecaptcha
              .execute("6Le4dpgaAAAAAOfaTgv75r2SYLkYC0CVVY3Lvcrv", {
                action: "submit",
              })
              .then(function (token) {
                if (!token) {
                  console.error("No token received");
                } else {
                  axios
                    .post(apiUrl, {
                      ...values,
                      message: "Assembly Website Contact Form Request",
                      "g-recaptcha-response": token,
                      source_page_url: window.location.href,
                    })
                    .then(() => {
                      setStatus("success");
                      setSubmitting(false);

                      if (window.dataLayer) {
                        window.dataLayer.push({
                          event: "formSubmit",
                          formType: "Lead Form Complete",
                          formName: "Contact Us",
                        });
                      }
                    })
                    .catch(() => {
                      setStatus("apiError");
                      setSubmitting(false);
                    });
                }
              });
          });
        }
      }}
    >
      {({ values, isSubmitting, status }) =>
        status === "success" ? (
          <Paragraph>{successMessage}</Paragraph>
        ) : (
          <Form>
            <FormFieldContainer $isFocused={focusedField === "name"}>
              <Label htmlFor="name">Your Name (required)</Label>
              <Input
                id="name"
                type="text"
                name="name"
                value={values.name}
                innerRef={nameInputRef}
                onFocus={() => {
                  setFocusedField("name");
                }}
              />
              <Error name="name" component="div" />
            </FormFieldContainer>
            <FormFieldContainer $isFocused={focusedField === "email"}>
              <Label htmlFor="email">Work Email (required)</Label>
              <Input
                id="email"
                type="text"
                name="email"
                innerRef={emailInputRef}
                value={values.email}
                onFocus={() => {
                  setFocusedField("email");
                }}
              />
              <Error name="email" component="div" />
            </FormFieldContainer>
            <FormFieldContainer>
              <Label htmlFor="company">Company Name (Required)</Label>
              <Input
                id="company"
                type="text"
                name="company"
                value={values.company}
                onFocus={() => {
                  setFocusedField("company");
                }}
              />
              <Error name="company" component="div" />
            </FormFieldContainer>
            <Error name="message" component="div" />
            <Button type="submit" disabled={isSubmitting}>
              Submit
            </Button>
            {status === "apiError" && (
              <Paragraph $colour="red">
                Sorry, there was an error on the server. Please try again.
              </Paragraph>
            )}
          </Form>
        )
      }
    </Formik>
  );
}

export default FormikForm;
